// SubmitTicketPage.jsx — M3. Change-request / issue submission form against
//   POST /api/me/tickets  { type, title, body, submitLang, projectGroupId,
//                           targetDocKey?, group?, targetDocId?,
//                           assigneeId?, derivedFrom? }   // SB-190: type 'assigned' only
// The submitter is the session account (server-side); any account field in the
// body is ignored. Project groups come from GET /api/project-groups (the form
// knows slugs; the backend needs the integer projectGroupId).
//
// Responses handled: 201 → success + link to My tickets; 403 FORBIDDEN → the
// job post lacks the submit_ticket right; 400 RULE_VIOLATION → surface the
// backend message (e.g. spec_change needs a target document).
//
// spec_change target (B5/OQ-A3): the canonical target is the language-neutral
// docKey. The doc browser's "Propose a change" deep-links here with the docKey +
// title + group prefilled (a read-only chip); the backend authorizes it by browse
// right. A numeric targetDocId remains as an advanced fallback. bug / maintenance
// / tech_debt / incident need no target document.
import { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import api, { ApiError } from '../api/client.mjs';
import { errKey, ruleKey } from '../api/errText.mjs';
import { hasMoreNote } from '../api/paging.mjs';
import { BACKLOG_PICKER_LIMIT, relatedBacklogHelper } from './workDays.mjs';
import { firstGroup } from '../api/groups.mjs';
import { useAuth } from '../auth/AuthContext.jsx';
import AppSnackbar, { useNotify } from '../components/AppSnackbar.jsx';
import HelpLink from '../components/HelpLink.jsx';
import PolishButton from '../components/PolishButton.jsx';
import AttachmentPicker, { AttachmentNotesBlock } from '../components/AttachmentPicker.jsx';
import { useLang, useT } from '../i18n/index.mjs';
import { bodyFormatFromToggle } from '../components/bodyFormat.mjs';
import {
  FAILED_ONLY,
  doneCount,
  failedItems,
  keepUnsent,
  mergeNoteRows,
  noteRows,
  orphanNoteItems,
  readyItems,
  renderNotesBlock,
  uploadItems,
} from './attachments.mjs';
import { TICKET_TYPES } from './ticketMeta.mjs';
import { normalizeRecipients, recipientOptionLabel } from './messages.mjs';
import { SLA_SAME_DAY_THRESHOLD, deadlineBar } from './responseSla.mjs';
import ResponseDeadlineBar from '../components/ResponseDeadlineBar.jsx';
import { useLoad } from '../hooks/useLoad.mjs';

const SUBMIT_LANGS = ['zh', 'en'];

export default function SubmitTicketPage() {
  const t = useT();
  // 第一百零一批（多项目制）：默认选中会话当前组；列表本身已按成员过滤。
  // SB-190：account 用来门控「指派单」这一项——只有 approve 位 ∥ 管理员能提
  // （后端照样 403；这里只是不摆一颗注定失败的按钮）。
  const { currentGroup, account } = useAuth();
  const { lang } = useLang();
  // B5: deep-link prefill from the doc browser ("propose a change to this doc").
  const prefill = useLocation().state || {};

  const [groups, setGroups] = useState({ status: 'loading', list: [], error: '' });
  const [groupId, setGroupId] = useState('');
  // SB-190：带着 derivedFrom 深链进来的，就是要开一张指派单——与 docKey ⇒ spec_change 同一条路。
  const [type, setType] = useState(prefill.docKey ? 'spec_change' : prefill.derivedFrom ? 'assigned' : 'bug');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  // 第八十八批：正文默认按 markdown 渲染；勾上这项＝这条正文按纯文本显示。
  // 缺省是"渲染"，因为绝大多数正文本来就是用 md 语法写的（`--body-file` 收的就是
  // 一份 .md），而此前它们一路以原始标记的样子显示给读者。
  const [bodyPlainText, setBodyPlainText] = useState(false);
  const [submitLang, setSubmitLang] = useState(lang);
  const [targetDocId, setTargetDocId] = useState('');
  // B5: the canonical language-neutral docKey target (+ its display label + group slug).
  const [targetDocKey, setTargetDocKey] = useState(prefill.docKey || '');
  const [targetDocLabel, setTargetDocLabel] = useState(prefill.title || prefill.docKey || '');
  const [targetDocGroup, setTargetDocGroup] = useState(prefill.group || '');

  // SB-025 (reactivated): attachments for any type + the asset type's
  // related-BL convention field (association by body header, not spec link).
  // 第五十六批：files 从裸 File[] 升级为带上传结局的挑选项（见 attachments.mjs），
  // 因为部分失败之后必须能看出**哪一个**没上去、并且只重试它。
  const [files, setFiles] = useState([]);
  // 部分失败后重试要打到**同一张已经建好的工单**上——否则重试就变成"再交一张工单"，
  // 正是 AST-000008/9 那个事故。工单号留在这里，重试按钮据此上传。
  const [retryNo, setRetryNo] = useState('');
  const [relatedBacklog, setRelatedBacklog] = useState('');
  // ── SB-190 §8（第二百四十五批）：指派单分支的两个字段 ──────────────────────
  // assignee 是收件人候选里的**那一行对象**（提交 id，不是任何名字——SB-147 的病灶），
  // derivedFrom 是原单编号（P3：只指回，原单不动）；可由 location.state 预填，
  // 与 prefill.docKey 同一条路。
  const [assignee, setAssignee] = useState(null);
  const [derivedFrom, setDerivedFrom] = useState(prefill.derivedFrom || '');
  // SB-046: which ticket types exist is server-authoritative (the ONE
  // registry feeds GET /api/ticket-types); the local TICKET_TYPES list is the
  // offline/error fallback so the form never goes empty.
  const [serverTypes, setServerTypes] = useState(TICKET_TYPES);
  useEffect(() => {
    let alive = true;
    api.ticketTypes()
      .then((r) => {
        if (!alive) return;
        const list = Array.isArray(r?.types) ? r.types.map((x) => x.type) : [];
        if (list.length) setServerTypes(list);
      })
      .catch(() => { /* fallback list stays */ });
    return () => {
      alive = false;
    };
  }, []);
  // SB-190 §0-6：谁能提指派单＝approve 位 ∥ 管理员（account_json.mayApprove 已是
  // 这两者之并）。门在**类型**上而不是在字段上：一个提不了的类型出现在下拉里，只会让
  // 人填完整张表才拿到 403。account 缺席（首帧）⇒ 先藏，与 /approvals 入口同姿态。
  const mayIssueAssigned = Boolean(account?.mayApprove || account?.isAdmin);
  const pickableTypes = mayIssueAssigned ? serverTypes : serverTypes.filter((x) => x !== 'assigned');
  // 深链带着 derivedFrom 进来、却没有提指派单的资格（外包跟着发出方的链接来了）：
  // 初值已经是 'assigned'，而它不在 pickableTypes 里——MUI Select 值域外、表单只能以
  // 403 收场，正是上面那道门要防的「注定失败的按钮」。account 首帧缺席，所以只能等它
  // 到位后再改，不能在 useState 的初值里判（第二百四十五批复审）。
  useEffect(() => {
    if (type === 'assigned' && account && !mayIssueAssigned) setType('bug');
  }, [type, account, mayIssueAssigned]);

  // 受指派人候选：复用 SB-169 站内信收件人名单（同组 ∪ 曾给我写过信的人）。只在选了
  // 「指派单」时取（其余七种类型一次请求都不该发，同 asset 分支的 myBacklogs），过滤
  // 在本地。端点失败即空数组——后端只校验「活跃账号」，名单只是选择器。
  const recipientLoad = useLoad(
    () => (type === 'assigned' ? api.messageRecipients() : Promise.resolve(null)),
    [type]);
  const recipients = recipientLoad.data ? normalizeRecipients(recipientLoad.data) : [];

  // SB-056 → batch 42 ruling: picker options are ALL active BL- of the group,
  // grouped "related to me" first (strict stakeholder filtering left the whole
  // dropdown empty for anyone with no assignment — freeSolo shows no popup at
  // zero options, which reads as broken). Degrades to [] (plain typing) if the
  // endpoint is unavailable; freeSolo keeps manual entry either way.
  const [assetBacklogs, setAssetBacklogs] = useState([]);
  const [assetBacklogsMore, setAssetBacklogsMore] = useState(null);   // SB-159：hasMoreNote 或 null
  // SB-159 复审（N3）：取数失败要说出来——此前它被吞成空表、配一句照常的 helper，与「这个组没有工作项」
  // 长得一模一样（口径 3）。freeSolo 照旧：取不到也能手输编号。
  const [assetBacklogsFailed, setAssetBacklogsFailed] = useState(false);

  useEffect(() => {
    if (type !== 'asset' || groupId === '') {
      // 三件一起复位：换了类型／组再回来时，上一回的「还有更多」或「没取回来」不该还挂在 helper 上。
      setAssetBacklogs([]);
      setAssetBacklogsMore(null);
      setAssetBacklogsFailed(false);
      return undefined;
    }
    let alive = true;
    // SB-159（第三百八十二批）：页长取服务端上限，并记下 hasMore——下拉里没有的那一项，可能只是
    // 不在这一页（freeSolo 仍允许手输编号）。
    api.myBacklogs(groupId, { lang, limit: BACKLOG_PICKER_LIMIT })
      .then((r) => {
        if (!alive) return;
        const rows = Array.isArray(r?.backlogs) ? r.backlogs : [];
        // Contiguous groups for Autocomplete's groupBy: mine before others.
        setAssetBacklogs(rows.slice().sort((a, b) => (b.isStakeholder ? 1 : 0) - (a.isStakeholder ? 1 : 0)));
        setAssetBacklogsMore(hasMoreNote(r));
        setAssetBacklogsFailed(false);
      })
      .catch(() => {
        if (alive) { setAssetBacklogs([]); setAssetBacklogsMore(null); setAssetBacklogsFailed(true); }
      });
    return () => {
      alive = false;
    };
  }, [type, groupId, lang]);

  const [busy, setBusy] = useState(false);
  // SB-067 一期：额度由**页面级**持有——同页两个润色按钮共享同一个池，
  // 各自去查会看到互相不一致的剩余数。
  const [polishQuota, setPolishQuota] = useState(null);
  const [fieldError, setFieldError] = useState('');
  const [ruleError, setRuleError] = useState('');
  // { no, uploaded, failed, rows, orphans } —— rows 是**上传成功**的附件的说明行
  // （attachments.mjs noteRows）。存行不存渲染结果：重试上传后要按附件 id 归并，
  // 存字符串就得先把它解析回来，多一道能出错的工序。
  const [success, setSuccess] = useState(null);
  const { snack, notify, clearSnack } = useNotify();

  // Load selectable project groups; default to the session's current group.
  // 第一百零一批（对抗审查修）：**不覆盖手选**。此前 deps 带 t，语言一切换 effect
  // 重跑就把用户刚手选的组重置回默认（对抗审查坐实）。改为只在「当前值已不在列表
  // 里」时才落默认——手选的合法组因此活过语言切换与上下文变动；t 从 deps 移除
  // （只在 catch 里用，取一次即可）。
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api.projectGroups();
        if (!alive) return;
        const list = Array.isArray(data?.groups) ? data.groups : [];
        setGroups({ status: 'ready', list, error: '' });
        setGroupId((prev) => {
          if (prev !== '' && list.some((x) => String(x.id) === prev)) return prev;  // 手选仍有效，留住
          const cur = currentGroup && list.some((x) => x.id === currentGroup.id)
            ? currentGroup : firstGroup(data);
          return cur?.id != null ? String(cur.id) : prev;
        });
      } catch {
        if (!alive) return;
        setGroups({ status: 'error', list: [], error: t('submit.error.groupsUnavailable') });
      }
    })();
    return () => {
      alive = false;
    };
  }, [currentGroup?.id]);   // t 刻意不在 deps——它只在 catch 里取一次，进 deps 会覆盖手选

  const groupsUnavailable = groups.status !== 'ready' || groups.list.length === 0 || groupId === '';

  // 工单已建好但还有附件没上去：此时**不能**让人再点提交——那会开出第二张工单，
  // 把这次的失败变成一次重复提交（AST-000009 的成因）。先重试或删掉失败项。
  const stuckFiles = failedItems(files).length;
  // 提交之后**新挑**的文件（下一张工单的材料）。它们绝不能跟着重试跑到 retryNo 上去，
  // 而且屏幕上要有个数——这是第五十七批 D1 的两半：判据一半、说明一半。
  const pendingNew = readyItems(files).length;
  const blockedByRetry = Boolean(retryNo) && stuckFiles > 0;

  // 只重传失败项，打到 retryNo 那张工单上。
  // FAILED_ONLY 是这里的要害：默认的 UNSENT 会把用户提交后新挑的照片一并传到**上一张**
  // 工单，随后 keepUnsent() 再把它们从表单里清掉——文件既不在表单里，也不在它该去的工单上。
  const onRetryUpload = async () => {
    if (!retryNo || busy) return;
    setBusy(true);
    try {
      const out = await uploadItems(files, (f, note) => api.uploadTicketAttachment(retryNo, f, note), FAILED_ONLY);
      const ok = doneCount(out);
      const bad = failedItems(out).length;
      if (ok > 0) notify(t('submit.attach.uploaded', { n: String(ok) }), 'success');
      if (bad > 0) notify(t('submit.attach.failed', { n: String(bad) }), 'error');
      setFiles(keepUnsent(out));
      if (bad === 0) setRetryNo('');
      setSuccess((s) =>
        s
          ? {
              ...s,
              uploaded: s.uploaded + ok,
              failed: bad,
              // 重试成功的那些**补进**清单（按附件 id 归并，不是再贴一份）：
              // 这一趟 out 里只有失败项被重传，第一趟已成功的项此刻在 files 里早被
              // keepUnsent 清掉了——不归并的话，重试一次就把前面所有说明冲没。
              rows: mergeNoteRows(s.rows, noteRows(out)),
              orphans: orphanNoteItems(out).length,
            }
          : s,
      );
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setFieldError('');
    setRuleError('');
    if (busy) return;

    if (!title.trim()) {
      setFieldError(t('submit.error.titleRequired'));
      return;
    }
    if (type === 'spec_change' && !targetDocKey.trim() && !targetDocId.trim()) {
      setFieldError(t('submit.error.targetDocRequired'));
      return;
    }
    // SB-190 §3.1：指派单没有受指派人就没有时钟——后端同样拒（fail-closed），
    // 这里先说人话。
    if (type === 'assigned' && !assignee) {
      setFieldError(t('submit.error.assigneeRequired'));
      return;
    }
    if (groupsUnavailable) {
      notify(t('submit.error.groupsUnavailable'), 'error');
      return;
    }

    // asset: the related BL- goes into the body header — SOP-09 processing and
    // any reader can see the association; deliberately NOT an OQ-C17 spec link.
    // Batch 43 (language rule): the prefix follows submitLang — the body is
    // user content in that language; SOP-09 recognizes both spellings.
    let bodyOut = body.trim();
    if (type === 'asset' && relatedBacklog.trim()) {
      const prefix = submitLang === 'en' ? 'Related backlog' : '关联工单';
      bodyOut = `${prefix}: ${relatedBacklog.trim()}\n\n${bodyOut}`;
    }
    const payload = {
      type,
      title: title.trim(),
      body: bodyOut,
      submitLang,
      projectGroupId: Number(groupId),
      // 显式给值而不是"纯文本时才带这个字段"：省略与"我选了 markdown"在请求里长得
      // 一样，而后端将来若改默认值，省略的那些会跟着变、显式的不会。
      bodyFormat: bodyFormatFromToggle(bodyPlainText),
    };
    if (type === 'spec_change') {
      if (targetDocKey.trim()) {
        // Preferred (B5/OQ-A3): the language-neutral docKey + its group (for the browse-gate).
        payload.targetDocKey = targetDocKey.trim();
        if (targetDocGroup) payload.group = targetDocGroup;
      } else {
        const n = Number(targetDocId.trim());
        if (Number.isInteger(n)) payload.targetDocId = n;
      }
    }
    // SB-190 §8：受指派人提交**账号 id**（后端 assigneeId 只认整数），派生自提交
    // **工单编号**（后端解析成原单 id；P3：原单不动）。空串不送——不送更诚实。
    if (type === 'assigned') {
      payload.assigneeId = Number(assignee.id);
      if (derivedFrom.trim()) payload.derivedFrom = derivedFrom.trim();
    }

    setBusy(true);
    try {
      const res = await api.submitTicket(payload);
      // SB-025: sequential attachment upload after the ticket exists; a partial
      // failure keeps the ticket and reports the failed count honestly.
      const out = await uploadItems(files, (f, note) => api.uploadTicketAttachment(res.no, f, note));
      const uploaded = doneCount(out);
      const failed = failedItems(out).length;
      // E1：说明清单**只**由上传结局产出。行里的 id / 落库文件名来自后端 201 回执，
      // 挑选项身上没有这两个字段——所以"照着挑的十个文件写十行"在这里根本写不出来，
      // 不是被过滤掉的。AST-000011 的十行表配九个附件，就此不可能再发生一次。
      setSuccess({
        no: res?.no,
        uploaded,
        failed,
        rows: noteRows(out),
        orphans: orphanNoteItems(out).length,
        // SB-190 §3.1 P2（第二百四十七批）：201 带本件的响应时钟（ASK- 才非 null）。发出方
        // 当场看到「截止 …、该日第 N 件」；1bd_defer 时再多说一句（P2 提示而不拦）。
        responseDeadline: res?.responseDeadline ?? null,
      });
      // 重试的靶子是这张工单，不是新的一张；全成功则解绑，免得下一次提交时
      // 重试按钮把新挑的文件打到上一张工单上。
      setRetryNo(failed > 0 ? res?.no || '' : '');
      if (failed > 0) notify(t('submit.attach.failed', { n: String(failed) }), 'error');
      // Reset the volatile fields; keep type/group/lang for a quick next submit.
      setTitle('');
      setBody('');
      setTargetDocId('');
      setTargetDocKey('');
      setTargetDocLabel('');
      // 第五十六批：**只清上传成功的**。原来这里是无条件 setFiles([])，一次失败就把
      // 那张照片从表单里抹掉，用户除了重开工单没有别的路——正是这次事故的第三个病灶。
      setFiles(keepUnsent(out));
      setRelatedBacklog('');
      // SB-190：下一张指派单几乎不会指给同一个人、更不会派生自同一张单——都清。
      setAssignee(null);
      setDerivedFrom('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setRuleError(t('submit.error.forbidden'));
      } else if (err instanceof ApiError && err.status === 400) {
        // RULE_VIOLATION / BAD_REQUEST — the stable rule id maps to the member's
        // language (batch 43); unknown rules keep the raw message (never vanish).
        const rk = ruleKey(err);
        setRuleError(rk && t(rk) !== rk ? t(rk) : err.message || t('submit.error.generic'));
      } else if (err instanceof ApiError && (err.status === 0 || err.status === 401)) {
        notify(t(errKey(err, { fallback: 'submit.error.generic' })), 'error');
      } else {
        setRuleError(t('submit.error.generic'));
      }
    } finally {
      setBusy(false);
    }
  };

  // A readable form column, widened from 720 with the fluid shell (the
  // description textarea benefits); still centered, never edge-to-edge.
  return (
    <Box sx={{ maxWidth: 960, mx: 'auto' }}>
      <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
        {t('submit.title')}
        <HelpLink section="ticket-types" />
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('submit.subtitle')}
      </Typography>

      <Card variant="outlined">
        <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
          {success ? (
            <>
            <Alert
              /* 有附件没上去还标成"成功"是在骗人——降级为 warning，用户才会去看下面的重试。 */
              severity={success.failed > 0 ? 'warning' : 'success'}
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" component={RouterLink} to="/my-tickets">
                  {t('submit.action.viewMine')}
                </Button>
              }
              onClose={() => setSuccess(null)}
            >
              {t('submit.success', { no: success.no })}
              {success.uploaded > 0
                ? ' ' + t('submit.attach.uploaded', { n: String(success.uploaded) })
                : ''}
              {/* SB-190 §3.1 P2：指派单的钟点条就摆在回执里——与受指派人在自己工单页看到的
                  是同一条（同一只 clock_for_item）。deferred（规则 1bd_defer）时多一句
                  「该日已有 N 件，本件自次一工作日起算」：N = rank − 1，阈值从 responseSla.mjs
                  取（与 defaults.h 同数），不在文案里手写。 */}
              {success.responseDeadline ? (() => {
                const bar = deadlineBar(success.responseDeadline);
                return bar ? (
                  <Box sx={{ mt: 0.75 }}>
                    <ResponseDeadlineBar deadline={success.responseDeadline} />
                    {bar.deferred ? (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {t('submit.assigned.deferred', {
                          n: String(Math.max(0, bar.rank - 1)),
                          k: String(SLA_SAME_DAY_THRESHOLD),
                        })}
                      </Typography>
                    ) : null}
                  </Box>
                ) : null;
              })() : null}
            </Alert>
            {/* 生成好的说明清单。它进不了正文——me 家族没有「改正文」的接口，而清单
                要等附件真的传上去才算得出来，那时正文已经落库。不假装贴进去了：
                摆出来、可复制、把原因讲清楚（诚实优先于方便）。 */}
            <Box sx={{ mb: 2 }}>
              <AttachmentNotesBlock
                block={renderNotesBlock(
                  success.rows,
                  t('submit.attach.notes.about', { n: String(success.rows.length) }),
                )}
                rows={success.rows.length}
                orphans={success.orphans}
                note={t('submit.attach.notes.bodyLocked')}
                onNotify={notify}
              />
            </Box>
            </>
          ) : null}

          {groups.status === 'error' ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {groups.error}
            </Alert>
          ) : null}

          <Box component="form" onSubmit={onSubmit} noValidate>
            <Stack spacing={2.5}>
              <TextField
                select
                label={t('submit.field.type')}
                value={type}
                onChange={(e) => setType(e.target.value)}
                disabled={busy}
                fullWidth
              >
                {pickableTypes.map((ty) => (
                  <MenuItem key={ty} value={ty}>
                    {/* label i18n with the raw-type fallback: a registry row
                        added server-side renders usably before its keys land */}
                    {t(`ticketType.${ty}`) === `ticketType.${ty}` ? ty : t(`ticketType.${ty}`)}
                  </MenuItem>
                ))}
              </TextField>

              <Box>
                <TextField
                  label={t('submit.field.title')}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={busy}
                  required
                  fullWidth
                  slotProps={{ htmlInput: { maxLength: 200 } }}
                />
                {/* SB-067 一期：润色在**拼接关联工单前缀之前**执行——正文里的
                    `关联工单: BL-…` 是 SOP-09 要解析的，不能进润色的输入。 */}
                <PolishButton value={title} scene="ticket_title" onChange={setTitle}
                              quota={polishQuota} onQuota={setPolishQuota} disabled={busy} />
              </Box>

              <Box>
                <TextField
                  label={t('submit.field.body')}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  disabled={busy}
                  fullWidth
                  multiline
                  minRows={4}
                />
                <PolishButton value={body} scene="ticket_body" onChange={setBody}
                              quota={polishQuota} onQuota={setPolishQuota} disabled={busy} />
                {/* 第八十八批：正文格式的出口。默认不勾＝按 markdown 渲染。 */}
                <FormControlLabel
                  sx={{ mt: 0.5 }}
                  control={<Checkbox size="small" checked={bodyPlainText} disabled={busy}
                    onChange={(e) => setBodyPlainText(e.target.checked)} />}
                  label={
                    <Box>
                      <Typography variant="body2">{t('submit.bodyIsPlainText')}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('submit.bodyIsPlainText.hint')}
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  select
                  label={t('submit.field.projectGroup')}
                  value={groupId}
                  onChange={(e) => setGroupId(e.target.value)}
                  disabled={busy || groups.status !== 'ready' || groups.list.length === 0}
                  fullWidth
                  helperText={groups.status === 'ready' && groups.list.length === 0 ? groups.error : ' '}
                >
                  {groups.list.map((g) => (
                    <MenuItem key={g.id} value={String(g.id)}>
                      {(lang === 'en' ? g.displayNameEn || g.displayName : g.displayName) ||
                        g.slug}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label={t('submit.field.submitLang')}
                  value={submitLang}
                  onChange={(e) => setSubmitLang(e.target.value)}
                  disabled={busy}
                  fullWidth
                  helperText=" "
                >
                  {SUBMIT_LANGS.map((lg) => (
                    <MenuItem key={lg} value={lg}>
                      {t(`lang.${lg}`)}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              {type === 'spec_change' ? (
                targetDocKey ? (
                  // B5: docKey chosen from the doc browser — read-only, discoverable target.
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                      {t('submit.field.targetDoc')}
                    </Typography>
                    <Chip
                      label={targetDocLabel || targetDocKey}
                      title={targetDocKey}
                      onDelete={busy ? undefined : () => {
                        setTargetDocKey('');
                        setTargetDocLabel('');
                      }}
                      variant="outlined"
                      sx={{ maxWidth: '100%' }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                      {t('submit.field.targetDoc.helper')}
                    </Typography>
                  </Box>
                ) : (
                  <TextField
                    label={t('submit.field.targetDocId')}
                    value={targetDocId}
                    onChange={(e) => setTargetDocId(e.target.value)}
                    disabled={busy}
                    fullWidth
                    type="number"
                    slotProps={{ htmlInput: { inputMode: 'numeric' } }}
                    helperText={t('submit.field.targetDocId.helper')}
                  />
                )
              ) : type === 'self_intro' ? (
                <Typography variant="caption" color="text.secondary">
                  {t('submit.hint.selfIntro')}
                </Typography>
              ) : type === 'asset' ? (
                <Stack spacing={1}>
                  <Typography variant="caption" color="text.secondary">
                    {t('submit.hint.asset')}
                  </Typography>
                  <Autocomplete
                    freeSolo
                    options={assetBacklogs}
                    groupBy={(o) =>
                      o.isStakeholder
                        ? t('submit.field.relatedBacklog.groupMine')
                        : t('submit.field.relatedBacklog.groupOther')
                    }
                    getOptionLabel={(o) => (typeof o === 'string' ? o : o.no)}
                    renderOption={(props, o) => (
                      <li {...props} key={o.no}>
                        <Typography variant="body2">
                          <Box component="span" sx={{ fontFamily: 'ui-monospace, monospace' }}>
                            {o.no}
                          </Box>
                          {' · '}
                          {o.title}
                          {/* OQ-A19 honest fallback: mark untranslated titles */}
                          {o.resolvedLang && o.resolvedLang !== lang ? ` (${o.sourceLang})` : ''}
                        </Typography>
                      </li>
                    )}
                    inputValue={relatedBacklog}
                    onInputChange={(e, v) => setRelatedBacklog(v || '')}
                    disabled={busy}
                    fullWidth
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('submit.field.relatedBacklog')}
                        placeholder="BL-000004"
                        error={assetBacklogsFailed}
                        helperText={relatedBacklogHelper({ failed: assetBacklogsFailed, more: assetBacklogsMore })
                          .map((h) => t(h.key, h.params)).join(' ')}
                      />
                    )}
                  />
                </Stack>
              ) : type === 'assigned' ? (
                /* SB-190 §8（第二百四十五批）：指派单分支，模板是上面的 asset 分支。
                   受指派人下拉与站内信编写器同一份名单、同一套标签（recipientOptionLabel），
                   选中提交的是账号 id；**不 freeSolo**——一个手打的名字不是任何合法的
                   assigneeId，后端也只认整数。派生自（P3）是原单编号，原单不动。
                   201 回执的 responseDeadline（第二百四十七批起）在上面的成功条里呈现。 */
                <Stack spacing={1}>
                  <Typography variant="caption" color="text.secondary">
                    {t('submit.hint.assigned')}
                  </Typography>
                  <Autocomplete
                    options={recipients}
                    value={assignee}
                    groupBy={(o) => t(o.sameGroup ? 'dm.new.toGroupSame' : 'dm.new.toGroupHistory')}
                    getOptionLabel={(o) => recipientOptionLabel(o, recipients)}
                    isOptionEqualToValue={(o, v) => o?.id === v?.id}
                    onChange={(e, v) => setAssignee(v)}
                    disabled={busy}
                    fullWidth
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('submit.field.assignee')}
                        required
                        helperText={t('submit.field.assignee.helper')}
                      />
                    )}
                  />
                  <TextField
                    label={t('submit.field.derivedFrom')}
                    value={derivedFrom}
                    onChange={(e) => setDerivedFrom(e.target.value)}
                    disabled={busy}
                    fullWidth
                    placeholder="BUG-000012"
                    slotProps={{ htmlInput: { style: { fontFamily: 'ui-monospace, monospace' } } }}
                    helperText={t('submit.field.derivedFrom.helper')}
                  />
                </Stack>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {t('submit.hint.simpleTypes')}
                </Typography>
              )}

              {/* SB-025 / 第五十六批: attachments (any type; sequential upload after
                  submit). 控件本身抽到 components/AttachmentPicker.jsx，与「我的提交」
                  详情面的补传共用一份——界面不得再比后端窄。 */}
              <AttachmentPicker
                items={files}
                onChange={setFiles}
                disabled={busy}
                /* 只在"有失败项且已绑定工单号"时给重试键——否则会把新挑的文件
                   打到上一张工单上，那正是要防的重复提交。 */
                onRetry={blockedByRetry ? onRetryUpload : null}
              />

              {blockedByRetry ? (
                <Alert severity="warning">
                  {t('submit.attach.blocked', { no: retryNo, n: String(stuckFiles) })}
                  {/* 重试期间「选择文件」仍然可用（用户就是要为下一张工单继续挑材料，
                      而且此时表单已被 blockedByRetry 挡住提交，禁掉挑选只会让人无事可做），
                      但新挑的文件去哪儿必须写明——否则"重试"看上去像是把列表里的都传上去。 */}
                  {pendingNew > 0
                    ? ` ${t('submit.attach.pendingNext', { no: retryNo, n: String(pendingNew) })}`
                    : ''}
                </Alert>
              ) : null}

              {fieldError ? <Alert severity="error">{fieldError}</Alert> : null}
              {ruleError ? <Alert severity="error">{ruleError}</Alert> : null}

              <Box>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={busy || groupsUnavailable || blockedByRetry}
                  startIcon={busy ? <CircularProgress size={18} color="inherit" /> : null}
                >
                  {t('submit.action.submit')}
                </Button>
              </Box>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      <AppSnackbar snack={snack} onClose={clearSnack} autoHideDuration={5000} />
    </Box>
  );
}
